import axios from 'axios';
import API_BASE_URL from './apiBaseUrl';

const REQUEST_TIMEOUT_MS = 15000;
const SAFE_RETRYABLE_METHOD = 'get';
const RETRYABLE_STATUS_CODES = new Set([429, 502, 503, 504]);
const MAX_GET_RETRIES = 2;
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const CSRF_COOKIE_CANDIDATES = ['csrftoken', 'csrf_token', 'XSRF-TOKEN'];
const CSRF_REFRESH_PATHS = ['/auth/csrf', '/csrf', '/csrf-token', '/auth/csrf-token'];
const GLOBAL_TOAST_DEDUPE_MS = 2000;
const API_LOG_SEPARATOR = '----------------------------';

let apiErrorNotifier = null;
let lastNotifiedError = { key: '', timestamp: 0 };

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
  },
});

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const getCookieValue = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const getMetaCsrfToken = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  return (
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ||
    document.querySelector('meta[name="csrf_token"]')?.getAttribute('content') ||
    null
  );
};

const getCsrfToken = () => {
  for (const cookieName of CSRF_COOKIE_CANDIDATES) {
    const value = getCookieValue(cookieName);
    if (value) {
      return value;
    }
  }

  return getMetaCsrfToken();
};

const isMutatingMethod = (method = '') =>
  ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase());

const isRetryableGetRequest = (config) =>
  (config?.method || '').toLowerCase() === SAFE_RETRYABLE_METHOD;

const isNetworkOrTimeoutFailure = (error) =>
  !error.response || error.code === 'ECONNABORTED';

const isCsrfFailure = (error) => {
  const status = error.response?.status;
  if (status !== 403 && status !== 419) {
    return false;
  }

  const detail = `${error.response?.data?.detail || ''}`.toLowerCase();
  const message = `${error.message || ''}`.toLowerCase();
  return status === 419 || detail.includes('csrf') || message.includes('csrf');
};

const refreshCsrfToken = async () => {
  for (const path of CSRF_REFRESH_PATHS) {
    try {
      await apiClient.get(path, {
        _skipRetries: true,
        _skipCsrfRetry: true,
      });
      const token = getCsrfToken();
      if (token) {
        return token;
      }
    } catch (_error) {
      // Try next endpoint candidate.
    }
  }

  return getCsrfToken();
};

export const normalizeApiError = (error) => {
  const status = error?.response?.status ?? null;
  const detail =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    'Request failed';

  const statusMessageMap = {
    400: 'Invalid request. Please review the submitted data.',
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to perform this action.',
    404: 'The requested resource was not found.',
    409: 'Message quota exceeded. Please upgrade your plan or renew your subscription.',
    410: 'This feature is no longer available.',
    413: 'The submitted payload is too large.',
    422: 'Validation failed. Please correct highlighted fields.',
    429: 'Too many requests. Please wait and try again.',
  };

  const uiMessage =
    statusMessageMap[status] ||
    (status && status >= 500
      ? 'Something went wrong on our side. Please try again.'
      : detail);

  return {
    status,
    detail,
    uiMessage,
    data: error?.response?.data ?? null,
    originalError: error,
  };
};

export const setApiErrorNotifier = (notifier) => {
  apiErrorNotifier = typeof notifier === 'function' ? notifier : null;
};

const notifyApiError = (normalizedError, config = {}) => {
  if (!apiErrorNotifier || config?.suppressGlobalErrorToast) {
    return;
  }

  const status = normalizedError?.status ?? 'unknown';
  const message = normalizedError?.uiMessage || normalizedError?.detail || 'Request failed';
  const dedupeKey = `${status}:${message}`;
  const now = Date.now();
  if (
    lastNotifiedError.key === dedupeKey &&
    now - lastNotifiedError.timestamp < GLOBAL_TOAST_DEDUPE_MS
  ) {
    return;
  }

  lastNotifiedError = {
    key: dedupeKey,
    timestamp: now,
  };
  apiErrorNotifier(normalizedError);
};

const getRequestUrl = (config = {}) => {
  const base = config.baseURL || '';
  const url = config.url || '';
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `${base}${url}`;
};

const logRequest = (config = {}) => {
  const method = (config.method || 'get').toUpperCase();
  const requestUrl = getRequestUrl(config);
  console.log(`[API] ${method} ${requestUrl}`);
};

const logResponse = (response) => {
  const method = (response?.config?.method || 'get').toUpperCase();
  const requestUrl = getRequestUrl(response?.config);
  console.log(`[API] ${method} ${requestUrl} -> ${response.status}`);
  console.log('[API RESPONSE]', response.data);
  console.log(API_LOG_SEPARATOR);
};

const logErrorResponse = (error) => {
  const method = (error?.config?.method || 'get').toUpperCase();
  const requestUrl = getRequestUrl(error?.config);
  const status = error?.response?.status ?? 'NO_STATUS';
  const payload = error?.response?.data ?? { message: error?.message || 'Request failed' };
  console.log(`[API] ${method} ${requestUrl} -> ${status}`);
  console.log('[API RESPONSE]', payload);
  console.log(API_LOG_SEPARATOR);
};

apiClient.interceptors.request.use((config) => {
  const updatedConfig = {
    ...config,
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      ...(config.headers || {}),
    },
  };

  const token = localStorage.getItem('token');
  // Public routes (e.g. invite-token onboarding) must not send a stored session JWT or the API may reject with 403.
  if (token && !updatedConfig.headers.Authorization && !updatedConfig.skipAuth) {
    updatedConfig.headers.Authorization = `Bearer ${token}`;
  }

  if (isMutatingMethod(updatedConfig.method)) {
    if (!updatedConfig.headers['Content-Type'] && updatedConfig.data !== undefined) {
      updatedConfig.headers['Content-Type'] = 'application/json';
    }

    const csrfToken = getCsrfToken();
    if (csrfToken && !updatedConfig.headers[CSRF_HEADER_NAME]) {
      updatedConfig.headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  logRequest(updatedConfig);
  return updatedConfig;
});

apiClient.interceptors.response.use(
  (response) => {
    logResponse(response);
    return response;
  },
  async (error) => {
    const config = error.config || {};

    if (
      !config._skipCsrfRetry &&
      isMutatingMethod(config.method) &&
      isCsrfFailure(error) &&
      !config._csrfRetried
    ) {
      const refreshedCsrfToken = await refreshCsrfToken();
      if (refreshedCsrfToken) {
        config._csrfRetried = true;
        config.headers = {
          ...(config.headers || {}),
          [CSRF_HEADER_NAME]: refreshedCsrfToken,
        };
        return apiClient.request(config);
      }
    }

    if (
      !config._skipRetries &&
      isRetryableGetRequest(config) &&
      (isNetworkOrTimeoutFailure(error) || RETRYABLE_STATUS_CODES.has(error.response?.status))
    ) {
      const retryCount = config._retryCount || 0;
      if (retryCount < MAX_GET_RETRIES) {
        config._retryCount = retryCount + 1;
        const baseDelayMs = 300 * 2 ** retryCount;
        const jitterMs = Math.floor(Math.random() * 250);
        await sleep(baseDelayMs + jitterMs);
        return apiClient.request(config);
      }
    }

    logErrorResponse(error);
    const normalizedError = normalizeApiError(error);
    notifyApiError(normalizedError, config);
    return Promise.reject(normalizedError);
  }
);

export default apiClient;
